import { Link } from 'react-router-dom';
import { ROUTE_NAMES } from '@tsgecart/config';
import { useProducts } from '../../catalog/hooks/use-catalog.js';
import { ProductCard, ProductCardSkeleton } from '../../catalog/components/product-card.js';
import { StorefrontErrorState } from '../../components/states.js';
import { Button } from '../../components/ui/button.js';

/**
 * The homepage's "Trending Deals" panel -- a white rounded panel that
 * overlaps the hero's lower edge (negative top margin + `relative z-10` so
 * it sits above the hero visually). Backed by the same `useProducts` hook
 * every other listing surface uses (`isFeatured: true`, capped at 4), never
 * fabricated data. This replaces what was previously the homepage's first
 * "Featured products" rail rather than duplicating that same query in two
 * places on one page.
 */
export function TrendingDealsSection() {
  const { data, isLoading, isError, refetch } = useProducts({ isFeatured: true, limit: 4, offset: 0 });

  if (!isLoading && !isError && (!data || data.length === 0)) return null;

  return (
    <section className="relative z-10 -mt-14 px-3 pb-4 sm:-mt-20 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl rounded-3xl border border-gray-100 bg-white p-5 shadow-xl sm:p-8">
        <div className="mb-5 flex items-center justify-between gap-4">
          <h2 className="text-xl font-extrabold tracking-tight text-slate-900 sm:text-2xl">Trending Deals</h2>
          <Link to={ROUTE_NAMES.shop}>
            <Button variant="primary" size="sm" className="sm:h-10 sm:px-5 sm:text-sm">
              Explore More
            </Button>
          </Link>
        </div>

        {isError ? (
          <StorefrontErrorState message="Couldn't load trending deals." onRetry={() => void refetch()} />
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {isLoading
              ? Array.from({ length: 4 }).map((_, index) => <ProductCardSkeleton key={index} />)
              : data!.map((product) => <ProductCard key={product.id} product={product} />)}
          </div>
        )}
      </div>
    </section>
  );
}
